import { GetPageByRouteQuery } from "apollo"
import { BlockRenderer } from "ui"
import { Culture, extractProperty, getBreadCrumbs } from "utils"
import { getBlockTypesNeedingData, loadBlockData } from "./blockDataLoaders"
import { defaultBlockComponents } from "./defaultBlockComponents"
import PageHeader from "./PageHeader"

type Block = {
  blockType: string
  id?: string
  key?: string
  [key: string]: unknown
}

export type SmartBlocksRendererProps = {
  blocks: Block[]
  locale: string
  culture: Culture
  pageData: GetPageByRouteQuery
  components?: Record<string, () => Promise<any>>
  isHomepage?: boolean
}

export default async function SmartBlocksRenderer({
  blocks,
  locale,
  culture,
  pageData,
  components,
  isHomepage,
}: SmartBlocksRendererProps): Promise<React.ReactElement> {
  const blockTypesNeedingData = getBlockTypesNeedingData(blocks)

  const extraData = await loadBlockData(blockTypesNeedingData, {
    locale,
    culture,
    pageData,
  })

  const mergedComponents = { ...defaultBlockComponents, ...components }

  // Find anchorMenu block to pass to tabsContent1
  const anchorBlock = blocks.find((b) => b.blockType === "anchorMenu") || null

  // Check if any block provides its own breadcrumbs (heroBanner) or title (mainTitleBlock)
  const hasHeroBanner = blocks.some((b) => b.blockType === "heroBanner")
  const hasMainTitleBlock = blocks.some((b) => b.blockType === "mainTitleBlock")

  // Always generate breadcrumbs from page data
  const breadCrumbs = getBreadCrumbs(pageData, locale)

  // Extract page title from pageInfoAndMetaTags for the fallback header
  // Only show title in PageHeader if no mainTitleBlock provides its own title
  const pageInfoAndMetaTags = extractProperty(
    pageData.page,
    "pageInfoAndMetaTags",
  )
  const pageTitle =
    !hasMainTitleBlock && pageInfoAndMetaTags?.[0]
      ? (extractProperty(pageInfoAndMetaTags[0], "titleBreadcrumbs") as
          | string
          | undefined)
      : undefined

  // Blocks with sticky headers - calculate cumulative offset
  const stickyBlockTypes = ["tabsContent", "tabsContent1"]
  const STICKY_HEADER_HEIGHT = 72 // Approximate height of sticky tab headers

  // Calculate sticky offset for each block based on previous sticky blocks
  const getStickyOffset = (currentIndex: number): number => {
    let offset = 0
    for (let i = 0; i < currentIndex; i++) {
      if (stickyBlockTypes.includes(blocks[i].blockType)) {
        offset += STICKY_HEADER_HEIGHT
      }
    }
    return offset
  }

  return (
    <div style={{ paddingBottom: 40, width: "100%" }}>
      {!hasHeroBanner && !isHomepage && (
        <PageHeader breadCrumbs={breadCrumbs} title={pageTitle} />
      )}
      {blocks.map((block, index) => {
        const blockKey = block.id || block.key || `${block.blockType}-${index}`

        // Build extra props for specific block types
        let blockSpecificProps: Record<string, unknown> = {}

        // Add stickyOffset for blocks with sticky headers
        if (stickyBlockTypes.includes(block.blockType)) {
          blockSpecificProps.stickyOffset = getStickyOffset(index)
        }

        if (block.blockType === "tabsContent1") {
          blockSpecificProps = {
            ...blockSpecificProps,
            anchorBlock,
            containerEnabled: true,
          }
        }

        return (
          <BlockRenderer
            key={blockKey}
            blockType={block.blockType}
            blockData={block}
            components={mergedComponents}
            locale={locale}
            culture={culture}
            props={{
              ...extraData,
              blockIndex: index,
              isSmartBlockRenderer: true,
              paddingTop: true,
              ...blockSpecificProps,
            }}
          />
        )
      })}
    </div>
  )
}
